import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spin, Empty } from 'antd';
import { BarChartOutlined, PieChartOutlined, LineChartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getGradeStatistics } from '../../services/grade';
import './Statistics.css';

interface StatusStat {
  status: string;
  count: number;
}

interface SemesterStat {
  semester: string;
  count: number;
}

interface CategoryStat {
  course_category: string;
  count: number;
}

interface SubmitStat {
  date: string;
  count: number;
}

interface StatisticsData {
  submitStats: SubmitStat[];
  statusStats: StatusStat[];
  semesterStats: SemesterStat[];
  categoryStats: CategoryStat[];
}

const CampusStatistics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StatisticsData>({
    submitStats: [],
    statusStats: [],
    semesterStats: [],
    categoryStats: [],
  });
  const navigate = useNavigate();

  const getUserRole = (): string => {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      return JSON.parse(userInfo).role || '';
    }
    return '';
  };
  const role = getUserRole();

  // 获取当前角色对应的成绩管理基础路径
  const getGradeBasePath = (): string => {
    return role === 'super_admin' ? '/admin/grades' : '/campus/grades';
  };

  // 双击统计数字跳转到对应记录列表
  const handleStatDoubleClick = (status?: string) => {
    if (status === 'pending') {
      // 待审核 -> 跳转到审核页面（角色不同路径不同）
      const reviewPath = role === 'super_admin'
        ? '/admin/grades?status=pending'
        : '/campus/grade-review';
      navigate(reviewPath);
    } else {
      // 已通过/已驳回/全部 -> 跳转到成绩管理页，携带 status 参数
      const basePath = getGradeBasePath();
      navigate(status ? `${basePath}?status=${status}` : basePath);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const res = await getGradeStatistics();
      setData(res);
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: '待审核',
      approved: '已通过',
      rejected: '已驳回',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      pending: '#FAAD14',
      approved: '#52C41A',
      rejected: '#FF4D4F',
    };
    return colorMap[status] || '#1890FF';
  };

  // 计算饼图数据
  const getPieChartData = () => {
    return data.statusStats.map((item) => ({
      name: getStatusText(item.status),
      value: item.count,
      color: getStatusColor(item.status),
    }));
  };

  // 计算柱状图数据
  const getBarChartData = () => {
    return data.semesterStats.slice(0, 10).reverse();
  };

  // 计算类别数据
  const getCategoryChartData = () => {
    return data.categoryStats;
  };

  if (loading) {
    return (
      <div className="statistics-loading">
        <Spin size="large" />
      </div>
    );
  }

  const hasData = data.statusStats.length > 0 || data.semesterStats.length > 0 || data.categoryStats.length > 0;

  return (
    <div className="statistics-container">
      <div className="statistics-header">
        <h1 className="statistics-title">
          <BarChartOutlined /> 成绩统计
        </h1>
        <p className="statistics-subtitle">
          {role === 'super_admin' ? '系统整体成绩数据统计' : '本校区成绩数据统计'}
        </p>
      </div>

      {!hasData ? (
        <Card className="statistics-card">
          <Empty description="暂无统计数据，请先录入成绩" />
        </Card>
      ) : (
        <>
          {/* 统计概览 */}
          <Row gutter={[16, 16]} className="statistics-row">
            <Col xs={24} sm={12} lg={6}>
              <Card
                className="stat-overview-card stat-overview-primary stat-clickable"
                onDoubleClick={() => handleStatDoubleClick()}
              >
                <div className="stat-overview-content">
                  <div className="stat-overview-value">
                    {data.statusStats.reduce((sum, item) => sum + item.count, 0)}
                  </div>
                  <div className="stat-overview-label">总成绩记录</div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card
                className="stat-overview-card stat-overview-warning stat-clickable"
                onDoubleClick={() => handleStatDoubleClick('pending')}
              >
                <div className="stat-overview-content">
                  <div className="stat-overview-value">
                    {data.statusStats.find((s) => s.status === 'pending')?.count || 0}
                  </div>
                  <div className="stat-overview-label">待审核</div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card
                className="stat-overview-card stat-overview-success stat-clickable"
                onDoubleClick={() => handleStatDoubleClick('approved')}
              >
                <div className="stat-overview-content">
                  <div className="stat-overview-value">
                    {data.statusStats.find((s) => s.status === 'approved')?.count || 0}
                  </div>
                  <div className="stat-overview-label">已通过</div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card
                className="stat-overview-card stat-overview-danger stat-clickable"
                onDoubleClick={() => handleStatDoubleClick('rejected')}
              >
                <div className="stat-overview-content">
                  <div className="stat-overview-value">
                    {data.statusStats.find((s) => s.status === 'rejected')?.count || 0}
                  </div>
                  <div className="stat-overview-label">已驳回</div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* 饼图 - 审核状态分布 */}
          <Row gutter={[16, 16]} className="statistics-row">
            <Col xs={24} lg={12}>
              <Card 
                className="statistics-card" 
                title={
                  <span>
                    <PieChartOutlined style={{ marginRight: 8 }} />
                    审核状态分布
                  </span>
                }
              >
                <div className="chart-container">
                  <div className="pie-chart-wrapper">
                    {data.statusStats.map((item, index) => {
                      const total = data.statusStats.reduce((sum, i) => sum + i.count, 0);
                      const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0';
                      return (
                        <div key={index} className="pie-item">
                          <div 
                            className="pie-color" 
                            style={{ backgroundColor: getStatusColor(item.status) }}
                          />
                          <div className="pie-label">{getStatusText(item.status)}</div>
                          <div className="pie-value">{item.count}</div>
                          <div className="pie-percentage">{percentage}%</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="pie-legend">
                    {data.statusStats.map((item, index) => (
                      <div key={index} className="legend-item">
                        <div 
                          className="legend-color" 
                          style={{ backgroundColor: getStatusColor(item.status) }}
                        />
                        <span className="legend-text">{getStatusText(item.status)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </Col>

            {/* 柱状图 - 学期分布 */}
            <Col xs={24} lg={12}>
              <Card 
                className="statistics-card" 
                title={
                  <span>
                    <BarChartOutlined style={{ marginRight: 8 }} />
                    学期成绩分布
                  </span>
                }
              >
                <div className="bar-chart-container">
                  {getBarChartData().map((item, index) => {
                    const maxCount = Math.max(...data.semesterStats.map((s) => s.count));
                    const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                    return (
                      <div key={index} className="bar-item">
                        <div className="bar-label">{item.semester}</div>
                        <div className="bar-wrapper">
                          <div 
                            className="bar-fill" 
                            style={{ height: `${height}%` }}
                          />
                        </div>
                        <div className="bar-value">{item.count}</div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </Col>
          </Row>

          {/* 课程类别分布 */}
          <Row gutter={[16, 16]} className="statistics-row">
            <Col xs={24}>
              <Card 
                className="statistics-card" 
                title={
                  <span>
                    <LineChartOutlined style={{ marginRight: 8 }} />
                    课程类别分布
                  </span>
                }
              >
                <div className="category-list">
                  {getCategoryChartData().map((item, index) => {
                    const total = data.categoryStats.reduce((sum, i) => sum + i.count, 0);
                    const percentage = total > 0 ? (item.count / total) * 100 : 0;
                    return (
                      <div key={index} className="category-item">
                        <div className="category-info">
                          <span className="category-name">{item.course_category || '未分类'}</span>
                          <span className="category-count">{item.count} 条</span>
                        </div>
                        <div className="category-progress">
                          <div 
                            className="category-progress-fill" 
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="category-percentage">{percentage.toFixed(1)}%</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default CampusStatistics;
